import { isEmpty } from 'lodash'
import pino, { Logger as PinoLogger } from 'pino'
import { ILogEntry, LogEntryBuilder } from './LogEntryBuilder'
import { ILogger, ILoggerBuildOptions, ILogPayload, LogLevel } from './LogTypes'
import { machineReadableLogs } from './machineReadableLogs'
import { PinoLoggerBuilder } from './PinoLoggerBuilder'

const DEFAULT_LOG_NAMESPACE: string = 'spare'

export class Logger implements ILogger {
  /**
   * Having a single root logger for the whole app that we create child loggers from, vs. creating tonnes of different
   * loggers, is more efficient in terms of both memory and CPU. Basically all the machinery to format logs, buffer
   * logs, send them to stdout, etc. gets created only one time and then used by all loggers, vs. being created a tonne
   * of times, and this machinery is heavy weight.
   */
  private static readonly rootLogger: PinoLogger = PinoLoggerBuilder.build()

  public static build(namespace: string, buildOptions?: ILoggerBuildOptions): ILogger {
    const name = this.buildName(namespace, buildOptions)
    const defaultPayload = { ...buildOptions?.parentLogger?.defaultPayload, ...buildOptions?.defaultPayload }
    const machineReadable = machineReadableLogs()
    return new Logger(name, this.rootLogger.child({ name }), machineReadable, defaultPayload)
  }

  private constructor(
    public readonly namespace: string,
    private readonly pinoLogger: pino.Logger,
    private readonly machineReadable: boolean,
    public readonly defaultPayload?: Record<string, unknown>,
  ) {}

  public error(message: string, payload?: ILogPayload): void {
    // These isLevelEnabled checks are just a small optimization - checking if a level is enabled is basically free,
    // building a log entry is more expensive
    if (this.isLevelEnabled(LogLevel.Error)) {
      this.pinoLogger.error(this.buildLogEntry(message, payload))
    }
  }

  public warn(message: string, payload?: ILogPayload): void {
    if (this.isLevelEnabled(LogLevel.Warn)) {
      this.pinoLogger.warn(this.buildLogEntry(message, payload))
    }
  }

  public info(message: string, payload?: ILogPayload): void {
    if (this.isLevelEnabled(LogLevel.Info)) {
      this.pinoLogger.info(this.buildLogEntry(message, payload))
    }
  }

  public trace(message: string, payload?: ILogPayload): void {
    if (this.isLevelEnabled(LogLevel.Trace)) {
      this.pinoLogger.trace(this.buildLogEntry(message, payload))
    }
  }

  public isLevelEnabled(level: LogLevel): boolean {
    return this.pinoLogger.isLevelEnabled(level)
  }

  private static buildName(namespace: string, buildOptions?: ILoggerBuildOptions): string {
    const parentNamespace = buildOptions?.parentLogger?.namespace ?? DEFAULT_LOG_NAMESPACE

    const service: string | undefined = process.env.SPARE_SERVICE_NAME ?? undefined
    return `${service ? `${service.toUpperCase()}:` : ''}${parentNamespace}:${namespace}`
  }

  private buildLogEntry(message: string, payload?: ILogPayload): ILogEntry {
    const combinedPayload = isEmpty(this.defaultPayload) ? (payload ?? {}) : { ...payload, ...this.defaultPayload }
    return LogEntryBuilder.build(message, combinedPayload, this.machineReadable)
  }
}
